import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { readBlockCtaIntent, resolveBlockCtaHref } from '../../lib/resolveSectionHref';
import type { BlockRenderProps } from '../../types';

export function HeroCollageStory({
  block,
  locale,
  defaultLocale = locale,
  isActive,
  activeEditTarget,
  editable,
  headingLevel = 'h1',
  pages,
  siteMode,
  allBlocks,
}: BlockRenderProps) {
  const Heading = headingLevel;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaPrimaryHref',
    fallback: 'browse',
  });
  const ctaPrimaryHref = resolveBlockCtaHref(ctaPrimaryIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaPrimaryHref',
    siteMode,
    pages,
    allBlocks,
  });
  const mainImage = pickMediaUrl(block, 'heroImage');
  const secondaryImage = pickMediaUrl(block, 'heroImageSecondary');
  const tertiaryImage = pickMediaUrl(block, 'heroImageTertiary');
  const mainSrc = mainImage ? buildSiteImageProxyUrl(mainImage, 960, 78) : undefined;
  const mainSrcSet = mainImage ? buildSiteImageSrcSet(mainImage, [480, 720, 960], 78) : undefined;
  const secondarySrc = secondaryImage ? buildSiteImageProxyUrl(secondaryImage, 480, 78) : undefined;
  const tertiarySrc = tertiaryImage ? buildSiteImageProxyUrl(tertiaryImage, 480, 78) : undefined;
  const target = isActive ? activeEditTarget : null;

  return (
    <section
      className={['ws-hero-zine', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="top"
    >
      <div className="ws-hero-zine__collage">
        {mainSrc ? (
          <EditableMedia
            fieldKey="heroImage"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-zine__img ws-hero-zine__img--main"
            src={mainSrc}
            srcSet={mainSrcSet}
            sizes={SITE_IMAGE_SIZES.heroZineMain}
            alt={heading || eyebrow || 'Story'}
            width={720}
            height={540}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        ) : (
          <div className="ws-hero-zine__img ws-hero-zine__img--main ws-hero-zine__placeholder" aria-hidden />
        )}
        {secondarySrc ? (
          <EditableMedia
            fieldKey="heroImageSecondary"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-zine__img ws-hero-zine__img--secondary"
            src={secondarySrc}
            sizes={SITE_IMAGE_SIZES.heroZineDetail}
            alt=""
            width={320}
            height={240}
            loading="lazy"
            decoding="async"
          />
        ) : null}
        {tertiarySrc ? (
          <EditableMedia
            fieldKey="heroImageTertiary"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-zine__img ws-hero-zine__img--tertiary"
            src={tertiarySrc}
            sizes={SITE_IMAGE_SIZES.heroZineDetail}
            alt=""
            width={320}
            height={240}
            loading="lazy"
            decoding="async"
          />
        ) : null}
      </div>
      <div id="about" className="ws-hero-zine__copy">
        <div className="ws-hero-zine__block">
          <EditableContent
            fieldKey="eyebrow"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-zine__eyebrow"
          >
            {eyebrow}
          </EditableContent>
          <EditableContent
            as={Heading}
            fieldKey="heading"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-zine__title"
          >
            {heading}
          </EditableContent>
          <EditableContent
            fieldKey="subheading"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-zine__lead"
          >
            {subheading}
          </EditableContent>
        </div>
        {ctaPrimary ? (
          <EditableContent
            as="a"
            fieldKey="ctaPrimary"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-zine__cta"
            href={ctaPrimaryHref}
          >
            {ctaPrimary}
          </EditableContent>
        ) : null}
      </div>
    </section>
  );
}
